import React, { useEffect, useState } from 'react';
import { Collapse, Progress, theme, ConfigProvider, Skeleton } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react';

import { StationReadinessCollapseContainer } from './styled';
import StationMissingDataFrame from './StationMissingDataFrame';
import { useStores } from '@/hooks/use-stores';
import { SpinCentered } from '@/comp';
import { StationReadinessScoreProps, handleDeviceEdit, handleAddImages } from './StationReadinessScore.utils';

const StationReadinessScore: React.FC<StationReadinessScoreProps> = observer(
  ({ title, buttonText, defaultActiveKey }) => {
    const { t } = useTranslation();
    const { token } = theme.useToken();
    const { stationStore, stationReadinessScoreStore, pidEditStore, mapBuilderStore } = useStores();
    const [activeKey, setActiveKey] = useState<string | string[]>(defaultActiveKey || []);

    const displayTitle = title || t('Station design readiness');
    const displayButtonText = buttonText || t('Show missing data');

    if (!stationReadinessScoreStore) {
      return <SpinCentered />;
    }

    const handleAddImagesLocal = (deviceId: string) => {
      handleAddImages(deviceId, mapBuilderStore, setActiveKey);
    };

    const handleAddModelSpec = async (deviceId: string) => {
      await handleDeviceEdit(deviceId, stationStore, pidEditStore, mapBuilderStore, setActiveKey);
    };

    useEffect(() => {
      const { customerId, siteId, id: stationId } = stationStore.currentStation;

      if (customerId && siteId && stationId) {
        stationReadinessScoreStore.getStationScore(customerId, siteId, stationId);
      }
    }, [stationStore.currentStation.customerId, stationStore.currentStation.siteId, stationStore.currentStation.id]);

     const { stationReadinessData, isLoading } = stationReadinessScoreStore;

     const actualPercentage = (!isLoading && stationReadinessData?.totalScore) || 0;

    const handleAddSensorData = async (deviceId: string) => {
      await handleDeviceEdit(deviceId, stationStore, pidEditStore, mapBuilderStore, setActiveKey);
    };

    const collapseItems = [
      {
        key: 'missing-data',
        label: (
          <Skeleton
            active
            loading={isLoading}
            paragraph={false}
            title={{ width: '250px' }}
            style={{ width: 'fit-content' }}
          >
            <div className="readiness-header">
              <div className="readiness-content">
                <div className="progress-container">
                  <Progress
                    type="circle"
                    percent={actualPercentage}
                    size={28}
                    strokeWidth={12}
                    strokeLinecap="square"
                    showInfo={false}
                    strokeColor={token.colorPrimary}
                    trailColor={token.colorBorderSecondary}
                  />
                  <div className="percentage-text">{actualPercentage.toFixed(1)}%</div>
                </div>
                <div className="description-text">{displayTitle}</div>
              </div>
              <div className="button-text">{displayButtonText}</div>
            </div>
          </Skeleton>
        ),
        children: (
          <div className="collapse-content-wrapper">
            <StationMissingDataFrame
              title={displayTitle}
              onAddModelSpec={handleAddModelSpec}
              onAddImages={handleAddImagesLocal}
             onAddSensorData={handleAddSensorData}
             isLoading={isLoading}
             stationData={stationReadinessData}
            />
          </div>
        ),
      },
    ];

    const collapseTheme = {
      components: {
        Collapse: {
          headerBg: token.colorBgContainer,
          contentBg: token.colorPrimaryBg,
          headerPadding: `0 ${token.paddingMD}px`,
          contentPadding: token.paddingMD,
        },
      },
    };

    const customExpandIcon = ({ isActive }) => (
      <div className="custom-expand-icon-wrapper">
        <DownOutlined className={`custom-expand-icon ${isActive ? 'active' : 'inactive'}`} />
      </div>
    );

    return (
      <StationReadinessCollapseContainer>
        <ConfigProvider theme={collapseTheme}>
          <Collapse
            items={collapseItems}
            activeKey={activeKey}
            onChange={setActiveKey}
            ghost
            size="small"
            expandIconPosition="end"
            expandIcon={customExpandIcon}
            className="custom-collapse"
          />
        </ConfigProvider>
      </StationReadinessCollapseContainer>
    );
  },
);

export default StationReadinessScore;
